/**
 * Entry point for development and production PWA builds.
 */
import 'regenerator-runtime/runtime';
import { createRoot } from 'react-dom/client';
import App from './App';
import React from 'react';
import { history } from './utils/history';
import { installFetchInterceptor } from './utils/fetchInterceptor';

// Intercept 401/403 responses globally so expired tokens redirect to login
// instead of silently breaking every page.
installFetchInterceptor();

/**
 * EXTENSIONS AND MODES
 * =================
 * pluginImports.js is dynamically generated from extension and mode
 * configuration at build time.
 *
 * pluginImports.js imports all of the modes and extensions and adds them
 * to the window for processing.
 */
import { modes as defaultModes, extensions as defaultExtensions } from './pluginImports';
import loadDynamicConfig from './loadDynamicConfig';

loadDynamicConfig(window.config).then(config_json => {
  // Reset Dynamic config if defined
  if (config_json !== null) {
    window.config = config_json;
  }

  // The browser no longer holds a PACS token (DICOMweb goes through the backend proxy);
  // clear any token left over from an older session so it can't be used to bypass it.
  try {
    sessionStorage.removeItem('pacs_access_token');
    localStorage.removeItem('pacs_access_token');
  } catch (e) {
    /* storage unavailable */
  }

  // DICOMweb goes through our backend proxy (per-clinic scope enforced server-side).
  // Derive the proxy URL from the build-time backend URL (REACT_APP_BACKEND_URL) so
  // staging and prod each use their own backend automatically; on any error we keep
  // whatever the config file set.
  try {
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (backend && window.config && Array.isArray(window.config.dataSources)) {
      window.config.dataSources.forEach(ds => {
        const c = ds && ds.configuration;
        // Target the DICOMweb data source by the presence of its root keys (they are
        // declared — blank — in default.js). Set them unconditionally from env here.
        if (c && ('qidoRoot' in c || 'wadoRoot' in c || 'wadoUriRoot' in c)) {
          c.qidoRoot = `${backend}/dicomweb/rs`;
          c.wadoRoot = `${backend}/dicomweb/rs`;
          c.wadoUriRoot = `${backend}/dicomweb/wado`;
        }
      });
    }
  } catch (e) {
    // keep the config file's values
  }

  /**
   * Combine our appConfiguration with installed extensions and modes.
   * In the future appConfiguration may contain modes added at runtime.
   *  */
  const appProps = {
    config: window ? window.config : {},
    defaultExtensions,
    defaultModes,
  };

  const container = document.getElementById('root');

  const root = createRoot(container);
  root.render(React.createElement(App, appProps));
});

export { history };
